import type { ReactNode } from "react";
import React, { createContext, useContext, useReducer } from "react";

import z from "zod";

import {
  markBusinessOnboardingComplete,
  markWorkerOnboardingComplete,
} from "@/queries/api/authApi";
import { completeOnboardingProfile } from "@/queries/api/onboardingApi";
import { completeOwnerOnboarding } from "@/queries/api/ownerOnboardingApi";
import { useLoginMutation, useSignupMutation } from "@/queries/hooks/authHooks";
import type { LoginData, SignupData, AuthResponse } from "@/schemas";
import { AuthResponseSchema } from "@/schemas";
import { sleep } from "@/utils";
import { clearBillingResolveDismissed } from "@/utils/billing";
import { Logger } from "@/utils/logger";

const NEW_WORKER_ONBOARDING_ENABLED =
  import.meta.env.VITE_NEW_WORKER_ONBOARDING === "true";

const NEW_EMPLOYER_ONBOARDING_ENABLED =
  import.meta.env.VITE_NEW_EMPLOYER_ONBOARDING === "true";

const log = Logger.scope("Auth");

// Schema for parsing user data from localStorage
export const UserDataSchema = z.object({
  userId: z.string(),
  email: z.string().email(),
  username: z.string(),
  role: z.union([z.literal("worker"), z.literal("employer")]),
  onboardingCompleted: z.boolean().optional().default(false),
  emailVerified: z.boolean().optional().default(false),
  phoneVerified: z.boolean().optional().default(false),
  phone: z.string().optional(),
  firstName: z.string().optional(),
  lastName: z.string().optional(),
  idToken: z.string().optional(),
  accessToken: z.string().optional(),
});

export type User = {
  userId: string;
  email: string;
  username: string;
  role: "worker" | "employer";
  onboardingCompleted?: boolean;
  emailVerified?: boolean;
  phoneVerified?: boolean;
  phone?: string;
  firstName?: string;
  lastName?: string;
};
export type AuthContextType = {
  isAuthenticated: boolean;
  user: User | null;
  error: string | null;
  login: (data: LoginData) => Promise<AuthResponse>;
  register: (data: SignupData) => Promise<AuthResponse>;
  logout: () => Promise<void>;

  completeOnboarding: () => Promise<void>;
  markEmailVerified: () => void;
  markPhoneVerified: () => void;
  updateUser: (updates: Partial<User>) => void;
};

type State = {
  isAuthenticated: boolean;
  user: User | null;
  error: string | null;
};

type Action =
  | {
      type: "SET_AUTH";
      user: User | null;
      isAuthenticated: boolean;
      error?: string | null;
    }
  | { type: "SET_ERROR"; error: string | null }
  | { type: "LOGOUT" };

function reducer(state: State, action: Action): State {
  switch (action.type) {
    case "SET_AUTH":
      return {
        ...state,
        user: action.user,
        isAuthenticated: action.isAuthenticated,
        error: action.error ?? null,
      };
    case "SET_ERROR":
      return { ...state, error: action.error };
    case "LOGOUT":
      return { isAuthenticated: false, user: null, error: null };
    default:
      return state;
  }
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthInternalStateContext = createContext<
  | { isAuthenticated: boolean; user: User | null; error: string | null }
  | undefined
>(undefined);
export const AuthInternalActionsContext = createContext<
  | {
      login: (data: LoginData) => Promise<AuthResponse>;
      register: (data: SignupData) => Promise<AuthResponse>;
      logout: () => Promise<void>;
      completeOnboarding: () => Promise<void>;
      markEmailVerified: () => void;
      markPhoneVerified: () => void;
    }
  | undefined
>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    Logger.trace("AuthContext not found");
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};

function readUserFromStorage(): User | null {
  try {
    if (typeof window === "undefined") return null;
    const raw = localStorage.getItem("pm-user");
    if (!raw) return null;
    const parsed = UserDataSchema.safeParse(JSON.parse(raw));
    return parsed.success ? parsed.data : null;
  } catch {
    return null;
  }
}

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  // ✅ hydrate synchronously from localStorage (runs before first render)
  const initialUser = readUserFromStorage();
  const [state, dispatch] = useReducer(reducer, {
    user: initialUser,
    isAuthenticated: !!initialUser,
    error: null,
  });

  const loginMutation = useLoginMutation();
  const registerMutation = useSignupMutation();

  React.useEffect(() => {
    const onStorage = (e: StorageEvent) => {
      if (e.key === "pm-user") {
        const u = readUserFromStorage();
        dispatch({ type: "SET_AUTH", user: u, isAuthenticated: !!u });
      }
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);

  const handleAuthFailure = React.useCallback(
    (message?: string) => {
      dispatch({
        type: "SET_AUTH",
        user: null,
        isAuthenticated: false,
        error: message || "Authentication failed",
      });
      log.error(message ?? "Authentication failed");
    },
    [dispatch],
  );

  const applyAuthSuccess = React.useCallback(
    (res: unknown) => {
      const parsed = AuthResponseSchema.safeParse(res);
      if (!parsed.success) {
        handleAuthFailure("Invalid authentication response");
        return;
      }
      const auth = parsed.data;
      if (!auth.user_info.roles.length) {
        handleAuthFailure("Account has no assigned role. Please contact support.");
        return;
      }
      const rawRole =
        auth.user_info.roles.find((role) => role !== "admin") || "worker";
      const role: "worker" | "employer" =
        rawRole === "employer" ? "employer" : "worker";

      const userData = {
        userId: auth.user_info.id,
        email: auth.user_info.email,
        username: auth.user_info.username,
        firstName: auth.user_info.first_name,
        lastName: auth.user_info.last_name,
        role,
        accessToken: auth.accessToken,
        emailVerified: auth.user_info.email_verified,
        phoneVerified: auth.user_info.phone_verified,
        onboardingCompleted: auth.user_info.onboarding_complete,
        enabled: auth.user_info.enabled,
        phone: auth.user_info.phone,
      };

      // Persist access token only; keep refresh token in sessionStorage
      try {
        sessionStorage.setItem("pm-refresh", auth.refreshToken);
      } catch {}
      localStorage.setItem("pm-user", JSON.stringify(userData));

      dispatch({
        type: "SET_AUTH",
        user: {
          userId: userData.userId,
          email: userData.email,
          username: userData.username,
          firstName: userData.firstName,
          lastName: userData.lastName,
          role: userData.role,
          emailVerified: userData.emailVerified,
          phone: userData.phone,
          phoneVerified: userData.phoneVerified,
          onboardingCompleted: userData.onboardingCompleted,
        },
        isAuthenticated: true,
        error: null,
      });

      Logger.setUser({
        id: userData.userId,
        email: userData.email,
        role: userData.role,
      });
      log.info("auth success");
    },
    [handleAuthFailure],
  );

  const login = React.useCallback(
    async (data: LoginData) => {
      dispatch({ type: "SET_ERROR", error: null });
      try {
        return await loginMutation.mutateAsync(data, {
          onSuccess: (res: AuthResponse) => {
            res.success
              ? applyAuthSuccess(res)
              : handleAuthFailure(res.message ?? undefined);
          },
          onError: (err: unknown) => {
            handleAuthFailure(
              err instanceof Error ? err.message : "Login error",
            );
          },
        });
      } catch (err: unknown) {
        handleAuthFailure(err instanceof Error ? err.message : "Login error");
        throw err as Error;
      }
    },
    [loginMutation, applyAuthSuccess, handleAuthFailure],
  );

  const register = React.useCallback(
    async (data: SignupData): Promise<AuthResponse> => {
      dispatch({ type: "SET_ERROR", error: null });
      try {
        return await registerMutation.mutateAsync(data, {
          onSuccess: (res: AuthResponse) => {
            res.success
              ? applyAuthSuccess(res)
              : handleAuthFailure(res.message ?? undefined);
          },
          onError: (err: unknown) => {
            Logger.error("Registration error", {
              error: err instanceof Error ? err.message : String(err),
            });
            handleAuthFailure(
              err instanceof Error ? err.message : "Registration error",
            );
          },
        });
      } catch (err: unknown) {
        Logger.error("Registration error", {
          error: err instanceof Error ? err.message : String(err),
        });
        handleAuthFailure(
          err instanceof Error ? err.message : "Registration error",
        );
        throw err as Error;
      }
    },
    [registerMutation, applyAuthSuccess, handleAuthFailure],
  );

  const logout = React.useCallback(async () => {
    await sleep(500);
    try {
      sessionStorage.removeItem("pm-refresh");
    } catch {}
    clearBillingResolveDismissed();
    localStorage.removeItem("pm-user");
    dispatch({ type: "LOGOUT" });
  }, []);

  const completeOnboarding = React.useCallback(async () => {
    if (typeof window === "undefined") return;
    const raw = localStorage.getItem("pm-user");
    if (!raw) return;

    try {
      const userData = UserDataSchema.parse(JSON.parse(raw));
      // Call server endpoint by role
      if (userData.role === "employer") {
        if (NEW_EMPLOYER_ONBOARDING_ENABLED) {
          await completeOwnerOnboarding();
        } else {
          await markBusinessOnboardingComplete();
        }
      } else if (NEW_WORKER_ONBOARDING_ENABLED) {
        // New onboarding contract: also re-validates server-side and flips the
        // Keycloak onboarding_complete attribute, same as the legacy endpoint.
        await completeOnboardingProfile();
      } else {
        await markWorkerOnboardingComplete();
      }
      const updatedUserData = {
        ...userData,
        onboardingCompleted: true,
      };
      localStorage.setItem("pm-user", JSON.stringify(updatedUserData));
      dispatch({
        type: "SET_AUTH",
        user: updatedUserData,
        isAuthenticated: true,
        error: null,
      });
    } catch (err) {
      log.error("Error completing onboarding", {
        error: String((err as any)?.message || err),
      });
      throw err as Error;
    }
  }, []);

  const markEmailVerified = React.useCallback(() => {
    if (typeof window === "undefined") return;
    const raw = localStorage.getItem("pm-user");
    if (!raw) return;

    try {
      const userData = UserDataSchema.parse(JSON.parse(raw));
      const updatedUserData = {
        ...userData,
        emailVerified: true,
      };
      localStorage.setItem("pm-user", JSON.stringify(updatedUserData));
      dispatch({
        type: "SET_AUTH",
        user: updatedUserData,
        isAuthenticated: true,
        error: null,
      });
    } catch (err) {
      log.error("Error updating email verification status", {
        error: String((err as any)?.message || err),
      });
    }
  }, []);

  const markPhoneVerified = React.useCallback(() => {
    if (typeof window === "undefined") return;
    const raw = localStorage.getItem("pm-user");
    if (!raw) return;

    try {
      const userData = UserDataSchema.parse(JSON.parse(raw));
      const updatedUserData = {
        ...userData,
        phoneVerified: true,
      };
      localStorage.setItem("pm-user", JSON.stringify(updatedUserData));
      dispatch({
        type: "SET_AUTH",
        user: updatedUserData,
        isAuthenticated: true,
        error: null,
      });
    } catch (err) {
      log.error("Error updating phone verification status", {
        error: String((err as any)?.message || err),
      });
    }
  }, []);

  // Add updateUser function
  const updateUser = React.useCallback((updates: Partial<User>) => {
    if (typeof window === "undefined") return;
    const raw = localStorage.getItem("pm-user");
    if (!raw) return;
    try {
      const userData = UserDataSchema.parse(JSON.parse(raw));
      const updatedUserData = {
        ...userData,
        ...updates,
      };
      localStorage.setItem("pm-user", JSON.stringify(updatedUserData));
      dispatch({
        type: "SET_AUTH",
        user: updatedUserData,
        isAuthenticated: true,
        error: null,
      });
    } catch (err) {
      log.error("Error updating user info in context", {
        error: String((err as any)?.message || err),
      });
    }
  }, []);

  return (
    <AuthInternalStateContext.Provider
      value={{
        isAuthenticated: state.isAuthenticated,
        user: state.user,
        error: state.error,
      }}
    >
      <AuthInternalActionsContext.Provider
        value={{
          login,
          register,
          logout,
          completeOnboarding,
          markEmailVerified,
          markPhoneVerified,
        }}
      >
        <AuthContext.Provider
          value={{
            isAuthenticated: state.isAuthenticated,
            user: state.user,
            error: state.error,
            login,
            register,
            logout,
            completeOnboarding,
            markEmailVerified,
            markPhoneVerified,
            updateUser, // <-- add here
          }}
        >
          {children}
        </AuthContext.Provider>
      </AuthInternalActionsContext.Provider>
    </AuthInternalStateContext.Provider>
  );
};
